import { ReactNode, useContext, useState, createContext, useEffect } from 'react';
import _ from 'lodash';
import { usePathname } from 'next/navigation';

import { NotificationProps } from '@stihl-design-system/components';

export interface Notification extends Omit<NotificationProps, 'children'> {
  id: string;
  message: ReactNode;
  pathNames?: string[];
  autoscroll?: boolean;
}

interface NotificationContext {
  notifications: Notification[];
  pageNotifications: Notification[];
  removeNotification: (...ids: string[]) => void;
  createInfoNotification: (notification: Notification) => void;
  createSuccessNotification: (notification: Notification) => void;
  createErrorNotification: (notification: Notification) => void;
  createWarningNotification: (notification: Notification) => void;
  clearNotifications: () => void;
}

export const NotificationContext = createContext<NotificationContext | undefined>(undefined);

export const NotificationContextProvider = ({ children }: { children: ReactNode }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const pathname = usePathname();

  useEffect(() => {
    clearNotifications();
  }, [pathname]);

  const mapNotification = (newNotifications: Notification[]) =>
    newNotifications.map((notification) => ({ ...notification, pathNames: notification.pathNames || [pathname] }));

  const clearNotifications = () => setNotifications((prev) => prev?.filter(({ pathNames }) => pathNames?.includes(pathname)));

  const remove = (...ids: string[]) => setNotifications((prev) => prev?.filter((notification) => !ids.includes(notification.id)));

  const createOrUpdate = (...newNotifications: Notification[]) => {
    const notificationsToCreate = _.differenceBy(newNotifications, notifications, 'id');
    setNotifications((prev) => [
      ...prev.map((notification) => ({ ...notification, ...newNotifications.find((n) => n.id === notification.id) })),
      ...mapNotification(notificationsToCreate),
    ]);
  };

  const createInfoNotification = (notification: Notification) => createOrUpdate({ ...notification, variant: 'info' });
  const createSuccessNotification = (notification: Notification) => createOrUpdate({ ...notification, variant: 'success' });
  const createErrorNotification = (notification: Notification) => createOrUpdate({ ...notification, variant: 'error', autoscroll: true });
  const createWarningNotification = (notification: Notification) => createOrUpdate({ ...notification, variant: 'warning' });

  const pageNotifications = notifications.reduceRight(
    (prev, { pathNames, ...notification }) => (pathNames?.includes(pathname) ? [...prev, notification] : prev),
    []
  );

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        pageNotifications,
        removeNotification: remove,
        createInfoNotification,
        createSuccessNotification,
        createErrorNotification,
        createWarningNotification,
        clearNotifications,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotificationContext = () => {
  const notificationContext = useContext(NotificationContext);

  if (!notificationContext) {
    throw new Error('useNotificationContext must be used in a component wrapped with NotificationContextProvider');
  }

  return notificationContext;
};
